import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import axios from 'axios';
import config from '../../config';

import './styles/propertyTable.css';
import '../../styles/pagination.css';

import Filter from '../general/filters';
import DeleteConfirmation from '../general/deleteConfirmation';
import PropertyDetailsPanel from './detailedPropertyPanel';
import EditUnitForm from '../forms/editUnitForm';
import Loading from '../general/loading';

const PropertyTable = ({ filterState }) => {
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [error, setError] = useState(null);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [formOverlayOpen, setFormOverlayOpen] = useState(false);

    const [editingProperty, setEditingProperty] = useState(null);
    const [propertyToDelete, setPropertyToDelete] = useState(null);

    // Filter states
    const [propertyFiter, setPropertyFiter] = useState('');
    const [bedroomsFilter, setBedroomsFilter] = useState('');
    const [bathroomsFilter, setBathroomsFilter] = useState('');

    // Sorting state
    const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

    // Pagination state
    const [page, setPage] = useState(1);
    const rowsPerPage = 10;

    const queryClient = useQueryClient();

    const { data: paginatedData = { results: [], totalPages: 0 }, isLoading: isTableLoading } = useQuery({
        queryKey: ['v2-units', page],
        queryFn: () => fetchPaginatedUnits(page, rowsPerPage),
        keepPreviousData: true,
    });

    const properties = useMemo(() => paginatedData.results || [], [paginatedData.results]);

    useEffect(() => {
        let filtered = properties;

        if (propertyFiter) filtered = filtered.filter((p) => p.property.name === propertyFiter);
        if (bedroomsFilter) filtered = filtered.filter((p) => p.bedrooms === parseInt(bedroomsFilter));
        if (bathroomsFilter) filtered = filtered.filter((p) => p.bathrooms === parseInt(bathroomsFilter));

        // Sorting logic
        if (sortConfig.key) {
            filtered = [...filtered].sort((a, b) => {
                const getValue = (obj, key) => {
                    return key.split('.').reduce((o, k) => (o ? o[k] : ''), obj);
                };

                const aValue = getValue(a, sortConfig.key);
                const bValue = getValue(b, sortConfig.key);

                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        setFilteredProperties(filtered);
    }, [propertyFiter, bedroomsFilter, bathroomsFilter, properties, sortConfig]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key) {
            // If already sorted in ascending, switch to descending
            if (sortConfig.direction === 'asc') {
                direction = 'desc';
            }
            // If already sorted in descending, reset to unsorted
            else if (sortConfig.direction === 'desc') {
                direction = ''; // No sort
            }
        }

        // Update the sort configuration based on the new direction
        setSortConfig({ key: direction === '' ? '' : key, direction });
    };

    const handleEdit = (property) => {
        setEditingProperty(property);
    };

    const promptDelete = (propertyId) => {
        setPropertyToDelete(propertyId);
    }

    const handleDelete = async () => {
        if (!propertyToDelete) console.log('No property to delete.');

        console.log('Deleting property:', propertyToDelete);

        try {
            await axios.delete(`${config.API_URL}/v2/unit/${propertyToDelete}`, {
                headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}
            });
            toast.success('Unit deleted successfully!');
            await queryClient.invalidateQueries(['v2-units']);
            setFormOverlayOpen(false);
            setPropertyToDelete(null);
        } catch (error) {
            console.error('Failed to delete property:', error);
            setError('Failed to delete unit.');
            toast.error('Failed to delete unit.');

            setFormOverlayOpen(false);
            setPropertyToDelete(null);
        }
    };

    const totalPages = paginatedData.totalPages || 1;
    const maxVisibleButtons = 10;
    const startPage = Math.floor((page - 1) / maxVisibleButtons) * maxVisibleButtons + 1;
    const endPage = Math.min(startPage + maxVisibleButtons - 1, totalPages);

    return (
        <div className="w-full full-page" style={{ minHeight: '77vh' }}>
            <div className="page-content">
                {isTableLoading && <Loading />}
                {error && <p className="error-message">{error}</p>}

                {filterState && properties && (
                    <div className="filter-container">
                        <Filter
                            placeholder="Property"
                            options={[...new Set(properties.map((p) => p.property.name))]}
                            value={propertyFiter}
                            onChange={setPropertyFiter}
                        />
                        <Filter
                            placeholder="Bedrooms"
                            options={[...new Set(properties.map((p) => p.bedrooms))].sort((a, b) => a - b)}
                            value={bedroomsFilter}
                            onChange={setBedroomsFilter}
                        />
                        <Filter
                            placeholder="Bathrooms"
                            options={[...new Set(properties.map((p) => p.bathrooms))].sort((a, b) => a - b)}
                            value={bathroomsFilter}
                            onChange={setBathroomsFilter}
                        />
                    </div>)}

                {!isTableLoading && !error && (
                    <div className='table-wrapper'>
                        <div className="page-table-container">
                            <table className="page-table">
                                <colgroup>
                                    <col style={{ width: '10%' }} /> {/* Unit column */}
                                    <col style={{ width: '15%' }} /> {/* Property column */}
                                    <col style={{ width: '35%' }} /> {/* Address column */}
                                    <col style={{ width: '10%' }} /> {/* Bedrooms column */}
                                    <col style={{ width: '10%' }} /> {/* Bathrooms column */}
                                    <col style={{ width: '15%' }} /> {/* Floor Area column */}
                                    <col style={{ width: '5%' }} /> {/* Actions column */}
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th className={sortConfig.key === 'unitNumber' ? 'active' : ''} onClick={() => handleSort('unitNumber')} style={{ textAlign: 'center' }}>
                                            Unit
                                            {sortConfig.key === 'unitNumber' && sortConfig.direction && (
                                                <span className={`sort-arrow ${sortConfig.direction === 'asc' ? 'sort-asc' : 'sort-desc'}`} />
                                            )}
                                        </th>
                                        <th className={sortConfig.key === 'property.name' ? 'active' : ''} onClick={() => handleSort('property.name')}>
                                            Property
                                            {sortConfig.key === 'property.name' && sortConfig.direction && (
                                                <span className={`sort-arrow ${sortConfig.direction === 'asc' ? 'sort-asc' : 'sort-desc'}`} />
                                            )}
                                        </th>
                                        <th className={sortConfig.key === 'property.address.street' ? 'active' : ''} onClick={() => handleSort('property.address.street')}>
                                            Address
                                            {sortConfig.key === 'property.address.street' && sortConfig.direction && (
                                                <span className={`sort-arrow ${sortConfig.direction === 'asc' ? 'sort-asc' : 'sort-desc'}`} />
                                            )}
                                        </th>
                                        <th className={sortConfig.key === 'bedrooms' ? 'active' : ''} onClick={() => handleSort('bedrooms')} style={{ textAlign: 'center' }}>
                                            Bedrooms
                                            {sortConfig.key === 'bedrooms' && sortConfig.direction && (
                                                <span className={`sort-arrow ${sortConfig.direction === 'asc' ? 'sort-asc' : 'sort-desc'}`} />
                                            )}
                                        </th>
                                        <th className={sortConfig.key === 'bathrooms' ? 'active' : ''} onClick={() => handleSort('bathrooms')} style={{ textAlign: 'center' }}>
                                            Bathrooms
                                            {sortConfig.key === 'bathrooms' && sortConfig.direction && (
                                                <span className={`sort-arrow ${sortConfig.direction === 'asc' ? 'sort-asc' : 'sort-desc'}`} />
                                            )}
                                        </th>
                                        <th className={sortConfig.key === 'floorArea' ? 'active' : ''} onClick={() => handleSort('floorArea')} style={{ textAlign: 'center' }}>
                                            Floor Area
                                            {sortConfig.key === 'floorArea' && sortConfig.direction && (
                                                <span className={`sort-arrow ${sortConfig.direction === 'asc' ? 'sort-asc' : 'sort-desc'}`} />
                                            )}
                                        </th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredProperties?.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" style={{ textAlign: 'center' }}>
                                                No data found.
                                            </td>
                                        </tr>
                                    ) : (
                                        (filteredProperties.map((property) => (
                                            <ClickableRow
                                                key={property._id}
                                                property={property}
                                                isSelected={selectedProperty && selectedProperty._id === property._id}
                                                onClick={() => setSelectedProperty(property)}
                                                onEdit={() => handleEdit(property)}
                                                onDelete={() => promptDelete(property._id)}
                                                setFormOverlayOpen={setFormOverlayOpen}
                                            />
                                        )))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination controls */}
                        {totalPages > 1 && (
                            <div className="pagination-controls mt-4">
                                <div className="pagination-buttons">
                                    <button
                                    className="pagination-button"
                                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                    disabled={page === 1}
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </button>

                                    {startPage > 1 && (
                                        <button
                                        className="pagination-page"
                                        onClick={() => setPage(startPage - 1)}
                                        >
                                            ...
                                        </button>
                                    )}

                                    {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                                        const pageNum = startPage + i;
                                        return (
                                            <button
                                            key={pageNum}
                                            className={`pagination-page ${page === pageNum ? 'active' : ''}`}
                                            onClick={() => setPage(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}

                                    {endPage < totalPages && (
                                        <button
                                        className="pagination-page"
                                        onClick={() => setPage(endPage + 1)}
                                        >
                                            ...
                                        </button>
                                    )}

                                    <button
                                    className="pagination-button"
                                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                                    disabled={page === totalPages}
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {selectedProperty && (
                    <PropertyDetailsPanel
                        property={selectedProperty}
                        onClose={() => setSelectedProperty(null)}
                    />
                )}

                {editingProperty && (
                    <EditUnitForm
                        propertyId={editingProperty.property._id} // Pass the propertyId
                        unitId={editingProperty._id} // Pass the unitId to EditUnitForm
                        onSave={() => queryClient.invalidateQueries(['v2-units'])} // Refresh the properties after save
                        onCancel={() => setEditingProperty(null)} // Close the modal
                    />
                )}

                {formOverlayOpen && (
                    <DeleteConfirmation
                        setIsOverlayOpen={setFormOverlayOpen}
                        onConfirm={handleDelete}
                    />
                )}
            </div>
        </div>
    );
};

const ClickableRow = ({ property, onClick, isSelected, onEdit, onDelete, setFormOverlayOpen }) => (
    <tr
        onClick={onClick}
        className={isSelected ? 'selected' : ''}
    >
        <td style={{ textAlign: 'center' }}>{property.unitNumber}</td>
        <td>{property.property.name}</td>
        <td>
            {property.unitNumber && `${property.property.address.streetNumber}, `}
            {property.property.address.street}, {property.property.address.city}, {property.property.address.state},{' '}
            {property.property.address.country} - {property.property.address.postalCode}
        </td>
        <td style={{ textAlign: 'center' }}>{property.bedrooms}</td>
        <td style={{ textAlign: 'center' }}>{property.bathrooms}</td>
        <td style={{ textAlign: 'center' }}>
            {property.floorArea != null ? `${property.floorArea} m²` : "-"}
        </td>
        <td>
            <div className="button-group">
                <button
                    className="action-buttons"
                    onClick={(e) => { e.stopPropagation(); onEdit(); }}
                >
                    <i className="fas fa-edit"></i>
                </button>
                <button
                    className="action-buttons"
                    onClick={(e) => { e.stopPropagation(); onDelete(); setFormOverlayOpen(true); }}
                >
                    <i className="fas fa-trash"></i>
                </button>
            </div>
        </td>
    </tr>
);

const fetchPaginatedUnits = async (page = 1, limit = 10) => {
    try {
        const response = await axios.get(`${config.API_URL}/v2/unit/query`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
            params: { page, limit },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching paginated units:', error);
        return { results: [], totalPages: 0, totalResults: 0 };
    }
};

export default PropertyTable;